import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePoints } from '../../helpers/hooks/pointsContext';
import { Spinner } from '../../helpers/UI/Spinner/Spinner';
import { useContestTime } from '../Rewards/RewardsPage/hooks/useContestTime';
import { useContestPrizes } from './hooks/useContestPrizes';
import { usePrizeDrawPastWinners } from './hooks/usePrizeDrawPastWinners';
import { PrizeDrawHero } from './components/PrizeDrawHero';
import { MonthlyDropCard } from './components/MonthlyDropCard';
import { ProgramRulesCard } from './components/ProgramRulesCard';
import { PastWinnersCard } from './components/PastWinnersCard';
import { mapContestPrizes } from './utils/prizeDrawUtils';
import './PrizeDrawPage.css';

const PrizeDrawPage: React.FC = () => {
  const navigate = useNavigate();
  const rulesAnchorRef = useRef<HTMLDivElement>(null);
  const { auth } = useAuth();
  const token = auth?.data.token;
  const { points, loading: pointsLoading } = usePoints();
  const { countdown } = useContestTime({
    token,
    enabled: true,
    mode: 'end',
  });
  const { prizes, loading: prizesLoading } = useContestPrizes(token);
  const { winners, loading: winnersLoading, dropLabel } = usePrizeDrawPastWinners(token);

  const tickets = Number(points?.data?.tickets?.totalAvailable ?? 0);
  const tiers = useMemo(() => mapContestPrizes(prizes), [prizes]);

  const scrollToProgramRules = () => {
    const target = rulesAnchorRef.current ?? document.getElementById('sv-program-rules');
    const scrollEl = document.querySelector('.scroll');
    if (target && scrollEl) {
      const start = scrollEl.scrollTop;
      const end = target.offsetTop - 20;
      const dur = 600;
      const startTime = performance.now();
      const step = (now: number) => {
        const p = Math.min((now - startTime) / dur, 1);
        const ease = p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;
        scrollEl.scrollTop = start + (end - start) * ease;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      return;
    }
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const root = document.getElementById('pg-save');
    root?.classList.add('on');
    return () => root?.classList.remove('on');
  }, []);

  if (pointsLoading) return <Spinner />;

  return (
    <div className='text-main'>
      <div className='pg' id='pg-save' style={{ gap: 45 }}>
        <PrizeDrawHero
          tickets={tickets}
          countdown={countdown}
          onEarnTickets={() => navigate('/rewards')}
          onProgramRules={scrollToProgramRules}
          onOpenTerms={scrollToProgramRules}
        />

        <div className='two-col sv-main-col'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <MonthlyDropCard tiers={tiers} loading={prizesLoading} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <PastWinnersCard winners={winners} loading={winnersLoading} dropLabel={dropLabel} />
            <div ref={rulesAnchorRef}>
              <ProgramRulesCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrizeDrawPage;
